import type { CellContext, ColumnDef } from '@tanstack/react-table';

import type { ScreenerDimension } from '@revelio/data-access';

import {
  applyCurrencyPrefix,
  applyScreenerValueFormat,
  formatScreenerColumnHeader,
  SCREENER_COLUMN_WHITELIST,
} from '../../../../../../shared/screener-formatting/screener-formatting-core';
import type { ScreenerTableRow } from '../../../DataProvider/utils/transform-screener-table-data';

export type ScreenerColumnKey = string;

export type ScreenerColumnMeta = {
  columnKey: string;
  sortKey?: string;
  columnNumber?: number;
  isSegmented: boolean;
};

export type ScreenerColumnDef = ColumnDef<ScreenerTableRow, unknown> & {
  accessorKey: string;
  header: string;
  meta: ScreenerColumnMeta;
  cell: (info: CellContext<ScreenerTableRow, unknown>) => string | number;
};

const COLUMN_WHITELIST_LOWER = SCREENER_COLUMN_WHITELIST.map((item) =>
  item.toLowerCase()
).sort((a, b) => b.length - a.length);

export const formatScreenerHeader = (
  name: string,
  view: ScreenerDimension
): string =>
  formatScreenerColumnHeader(name, view, {
    skipRateSuffix: name.includes('('),
  });

export const formatScreenerCellValue = (
  value: unknown,
  columnName: string,
  currencyCode = 'USD'
): string | number => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value !== 'number' && typeof value !== 'boolean') {
    return String(value);
  }

  const formattedValue = applyScreenerValueFormat(value, columnName);
  return applyCurrencyPrefix(formattedValue, columnName, currencyCode);
};

export const getScreenerColumnDefs = (
  data: ScreenerTableRow[],
  view: ScreenerDimension,
  currencyCode = 'USD'
): ScreenerColumnDef[] => {
  if (!data || data.length === 0) {
    return [];
  }

  const allColumnKeys = new Set<string>();
  data.forEach((row) => {
    // biome-ignore lint/suspicious/useIterableCallbackReturn: migrated from develop
    Object.keys(row).forEach((key) => allColumnKeys.add(key));
  });

  const keys = Array.from(allColumnKeys).filter((columnName) => {
    const lowerColumnName = columnName.toLowerCase();
    const isWhitelisted = COLUMN_WHITELIST_LOWER.some(
      (whitelistItem) =>
        lowerColumnName === whitelistItem ||
        lowerColumnName.startsWith(`${whitelistItem}_`)
    );

    return isWhitelisted;
  });

  const getBaseWhitelistKey = (columnName: string): string => {
    const lowerColumnName = columnName.toLowerCase();
    const matched = COLUMN_WHITELIST_LOWER.find(
      (whitelistItem) =>
        lowerColumnName === whitelistItem ||
        lowerColumnName.startsWith(`${whitelistItem}_`)
    );

    return matched ?? lowerColumnName;
  };

  const groupedColumns = keys.reduce<Record<string, string[]>>((acc, name) => {
    const baseColumnName = name.includes('growth_yoy')
      ? 'growthYoy'
      : (name.split('_')[0]?.trim() ?? name);

    return {
      ...acc,
      [baseColumnName]: [...(acc[baseColumnName] ?? []), name],
    };
  }, {});

  const columns: ScreenerColumnDef[] = Object.entries(groupedColumns).flatMap(
    ([_baseKey, group]) =>
      group.map((columnKey, index): ScreenerColumnDef => {
        const header = formatScreenerHeader(columnKey, view);
        const baseWhitelistKey = getBaseWhitelistKey(columnKey);
        const normalizedColumnKey = columnKey.toLowerCase();
        const isSegmented =
          normalizedColumnKey !== baseWhitelistKey &&
          normalizedColumnKey.startsWith(`${baseWhitelistKey}_`);

        const rawColumnName = columnKey.includes('growth_yoy')
          ? 'growth_yoy'
          : (columnKey.split('_')[0]?.trim() ?? columnKey);

        return {
          accessorKey: columnKey,
          header,
          cell: (info) => {
            const value = info.getValue();
            return formatScreenerCellValue(value, columnKey, currencyCode);
          },
          meta: {
            columnKey,
            sortKey: rawColumnName,
            columnNumber: index,
            isSegmented,
          },
        };
      })
  );

  return columns;
};
